import { AuthService } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7141/api/v1';

export interface StcBinGrpRelationDto {
    id: number;
    companyNo: string;
    masterBinGrpNo: number;
    childBinGrpNo: number;
    updatedBy?: string;
    lastUpdate?: string;
    createdBy?: string;
    created?: string;
}

export interface CreateStcBinGrpRelationRequest {
    companyNo: string;
    masterBinGrpNo: number;
    childBinGrpNo: number;
}

export interface UpdateStcBinGrpRelationRequest {
    // No editable fields for this relation table - only audit fields are updated automatically
}

class StcBinGrpRelationService {
    private baseUrl = `${API_BASE_URL}/stc-bin-grp-relations`;

    async getByCompanyAndGroups(companyNo: string, masterBinGrpNo: number, childBinGrpNo: number): Promise<StcBinGrpRelationDto | null> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${masterBinGrpNo}/${childBinGrpNo}`);
            if (!response.ok) {
                if (response.status === 404) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            return await response.json();
        } catch (error: any) {
            if (error.message?.includes('404')) {
                return null;
            }
            throw error;
        }
    }

    async getByCompanyNo(companyNo: string): Promise<StcBinGrpRelationDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/company/${companyNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByMasterGroup(companyNo: string, masterBinGrpNo: number): Promise<StcBinGrpRelationDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/master/${companyNo}/${masterBinGrpNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getByChildGroup(companyNo: string, childBinGrpNo: number): Promise<StcBinGrpRelationDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/child/${companyNo}/${childBinGrpNo}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getCurrent(): Promise<StcBinGrpRelationDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/current`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async getAll(): Promise<StcBinGrpRelationDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}`);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async create(request: CreateStcBinGrpRelationRequest): Promise<StcBinGrpRelationDto> {
        const response = await AuthService.authenticatedPost(`${this.baseUrl}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async update(companyNo: string, masterBinGrpNo: number, childBinGrpNo: number, request: UpdateStcBinGrpRelationRequest): Promise<StcBinGrpRelationDto> {
        const response = await AuthService.authenticatedPut(`${this.baseUrl}/${companyNo}/${masterBinGrpNo}/${childBinGrpNo}`, request);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }

    async delete(companyNo: string, masterBinGrpNo: number, childBinGrpNo: number): Promise<void> {
        const response = await AuthService.authenticatedDelete(`${this.baseUrl}/${companyNo}/${masterBinGrpNo}/${childBinGrpNo}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
    }

    async exists(companyNo: string, masterBinGrpNo: number, childBinGrpNo: number): Promise<boolean> {
        try {
            const response = await AuthService.authenticatedGet(`${this.baseUrl}/${companyNo}/${masterBinGrpNo}/${childBinGrpNo}`);
            return response.ok;
        } catch (error: any) {
            return false;
        }
    }

    async search(companyNo: string, searchTerm: string): Promise<StcBinGrpRelationDto[]> {
        const response = await AuthService.authenticatedGet(`${this.baseUrl}/search/${companyNo}?searchTerm=${encodeURIComponent(searchTerm)}`);
        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: response.statusText }));
            throw new Error(error.error || `HTTP error! status: ${response.status}`);
        }
        return await response.json();
    }
}

export const stcBinGrpRelationService = new StcBinGrpRelationService();
